import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Star, ShieldCheck, Droplets, MapPin, Lock, CheckCircle2, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { BIZ, SERVICES } from "@/content";
import { Swoosh } from "./Swoosh";
import { Reveal } from "./Reveal";

const EMPTY = { name: "", phone: "", email: "", suburb: "", service: "Other", urgency: "flexible", message: "" };
const URGENCY = [["emergency", "Emergency"], ["this_week", "This week"], ["flexible", "Flexible"]];
const input = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-base text-slate-900 outline-none transition-[border-color,box-shadow] placeholder:text-slate-500 focus:border-[#FF6B00] focus:ring-4 focus:ring-[#FF6B00]/15";

const Field = ({ label, req, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-semibold text-slate-800">{label} {req && <span className="text-[#E05A00]">*</span>}</span>
    {children}
  </label>
);

const Success = ({ name, onReset }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex min-h-[28rem] flex-col items-center justify-center text-center" data-testid="quote-form-success">
    <CheckCircle2 className="h-16 w-16 text-[#10B981]" />
    <h3 className="font-display mt-6 text-3xl font-extrabold text-[#0F2B5B]">Thanks{name ? `, ${name.split(" ")[0]}` : ""}!</h3>
    <p className="mt-3 max-w-sm text-slate-600">Your enquiry is in. We'll be in touch shortly. Need us urgently? Call <a className="font-bold text-[#E05A00]" href={BIZ.tel}>{BIZ.phone}</a>.</p>
    <button onClick={onReset} data-testid="quote-form-reset-button" className="mt-8 rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-[#0F2B5B] hover:border-[#FF6B00]">Send another enquiry</button>
  </motion.div>
);

const QuoteCard = ({ preset }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (preset?.service) setForm((f) => ({ ...f, service: preset.service }));
  }, [preset]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      setDone(form.name);
      setForm(EMPTY);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (done !== null) return <Success name={done} onReset={() => setDone(null)} />;

  return (
    <form onSubmit={submit} data-testid="quote-form" className="space-y-4">
      <div>
        <h3 className="font-display text-3xl font-extrabold text-[#0F2B5B]">Send us an enquiry</h3>
        <p className="mt-1 text-slate-600">Fill in the form below and we'll be in touch shortly.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name" req><input required minLength={2} className={input} placeholder="Your full name" value={form.name} onChange={set("name")} data-testid="quote-form-name-input" /></Field>
        <Field label="Phone number" req><input required type="tel" minLength={8} className={input} placeholder="Mobile number" value={form.phone} onChange={set("phone")} data-testid="quote-form-phone-input" /></Field>
        <Field label="Email (optional)"><input type="email" className={input} placeholder="Email address" value={form.email} onChange={set("email")} data-testid="quote-form-email-input" /></Field>
        <Field label="Suburb" req><input required minLength={2} className={input} placeholder="Your suburb" value={form.suburb} onChange={set("suburb")} data-testid="quote-form-suburb-input" /></Field>
      </div>
      <Field label="Service needed" req>
        <select className={input} value={form.service} onChange={set("service")} data-testid="quote-form-service-select">
          {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <div>
        <span className="mb-1.5 block text-sm font-semibold text-slate-800">How urgent is it?</span>
        <div className="grid grid-cols-3 gap-2" role="radiogroup">
          {URGENCY.map(([v, l]) => (
            <button type="button" key={v} role="radio" aria-checked={form.urgency === v} onClick={() => setForm((f) => ({ ...f, urgency: v }))} data-testid={`quote-form-urgency-${v}`}
              className={`rounded-xl border px-3 py-3 text-sm font-bold transition-colors duration-200 ${form.urgency === v ? "border-[#FF6B00] bg-[#FF6B00] text-white" : "border-slate-300 text-[#0F2B5B] hover:border-[#FF6B00]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <Field label="What can we help you with?" req>
        <textarea required minLength={5} rows={4} className={input} placeholder="Please describe the issue or service you need" value={form.message} onChange={set("message")} data-testid="quote-form-message-input" />
      </Field>
      <button disabled={busy} data-testid="quote-form-submit-button"
        className="shimmer flex w-full items-center justify-center gap-2 rounded-xl bg-[#E05A00] py-4 font-bold uppercase tracking-[0.12em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#C94F00] disabled:opacity-70">
        {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : "Send enquiry"}
      </button>
      <p className="flex items-center justify-center gap-2 text-sm text-slate-600"><Lock className="h-4 w-4" /> Your details are secure and will never be shared.</p>
    </form>
  );
};

const POINTS = [[Star, "5-Star Google Rated"], [ShieldCheck, `Licensed ${BIZ.licence}`], [Droplets, "Water Corporation Waterwise Plumber"], [MapPin, "Local Perth Business"]];

export const QuoteSection = ({ preset }) => (
  <section id="quote" className="grain bg-brand relative overflow-hidden py-24 text-white lg:py-32" data-testid="quote-section">
    <Swoosh className="absolute -right-24 top-0 h-full w-[55%] opacity-90" />
    <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 lg:grid-cols-12 lg:px-10">
      <Reveal className="lg:col-span-5">
        <h2 className="font-display text-4xl font-extrabold leading-[1] tracking-tight sm:text-6xl">Tell us what's going on.</h2>
        <p className="mt-6 max-w-md text-base text-white/85 md:text-lg">Send us a few details and we'll get back to you as soon as possible.</p>
        <ul className="mt-10 space-y-5">
          {POINTS.map(([Icon, l]) => <li key={l} className="flex items-center gap-4 text-lg font-semibold"><Icon className="h-6 w-6 text-[#FFB400]" />{l}</li>)}
        </ul>
        <div className="mt-12">
          <p className="flex items-center gap-3 text-white/85"><Phone className="h-5 w-5" /> Prefer to talk now?</p>
          <a href={BIZ.tel} className="font-display mt-1 block text-4xl font-extrabold text-[#FF8533] sm:text-5xl" data-testid="quote-section-phone">{BIZ.phone}</a>
          <a href={BIZ.mailto} className="mt-4 inline-block text-base text-white/85 underline-offset-4 hover:text-white hover:underline" data-testid="quote-section-email">{BIZ.email}</a>
          <p className="mt-2 text-sm text-white/70">{BIZ.hours} · {BIZ.emergency}</p>
        </div>
      </Reveal>
      <Reveal className="lg:col-span-7" delay={0.1}>
        <div className="rounded-[1.75rem] bg-white p-6 text-slate-900 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] sm:p-10">
          <QuoteCard preset={preset} />
        </div>
      </Reveal>
    </div>
  </section>
);
